import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BuyContractService } from '../buy-contract.service';
import { AutoBuyConfig, StockLease, StockLeaseStatus } from '../buy-contract.type';
import { ScreenshotPreview } from '../../../account/screenshot-preview/screenshot-preview';

export interface StockLeasesDialogData {
  config: AutoBuyConfig;
}

/** Leases move on the scale of a drain run, so a slow refresh is enough. */
const POLL_INTERVAL_MS = 15_000;

@Component({
  selector: 'app-stock-leases-dialog',
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTableModule,
    MatTooltipModule,
  ],
  templateUrl: './stock-leases-dialog.html',
  styleUrl: './stock-leases-dialog.scss',
})
export class StockLeasesDialog implements OnInit, OnDestroy {
  private service = inject(BuyContractService);
  private dialog = inject(MatDialog);
  private dialogRef = inject(MatDialogRef<StockLeasesDialog>);
  private snackBar = inject(MatSnackBar);
  readonly data = inject<StockLeasesDialogData>(MAT_DIALOG_DATA);

  displayedColumns = ['stock', 'status', 'coins', 'cards', 'timing', 'screenshots'];

  leases: StockLease[] = [];
  loading = true;
  error: string | null = null;

  fleet: AutoBuyConfig = this.data.config;

  private get ledgerId(): string {
    return this.data.config.buy_contract_id ?? '';
  }

  private pollTimer: ReturnType<typeof setTimeout> | null = null;
  private destroyed = false;

  ngOnInit(): void {
    this.load();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.clearTimer();
  }

  load(): void {
    this.service.getStockLeases(this.ledgerId).subscribe({
      next: res => {
        this.leases = res.items ?? [];
        this.loading = false;
        this.error = null;
        this.scheduleNextPoll();
      },
      error: err => {
        this.loading = false;
        this.error = err?.status === 404
          ? 'Auto-buy ledger not found.'
          : (err?.error?.error || 'Error loading stock leases');
      },
    });

    this.service.getAutoBuyConfig().subscribe({
      next: cfg => this.fleet = cfg,
      error: () => { /* the leases themselves are the point; keep what we have */ },
    });
  }

  private scheduleNextPoll(): void {
    this.clearTimer();
    if (this.destroyed) return;
    this.pollTimer = setTimeout(() => this.load(), POLL_INTERVAL_MS);
  }

  private clearTimer(): void {
    if (this.pollTimer !== null) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
  }

  viewScreenshot(lease: StockLease, type: 'start' | 'end'): void {
    this.snackBar.open(`Loading ${type} screenshot...`, undefined, { duration: 2000 });
    this.service.getStockLeaseScreenshot(this.ledgerId, lease.id, type).subscribe({
      next: blob => {
        const imageUrl = URL.createObjectURL(blob);
        const ref = this.dialog.open(ScreenshotPreview, {
          data: { imageUrl, imageBlob: blob },
          maxWidth: '95vw',
          maxHeight: '95vh',
          panelClass: 'screenshot-dialog',
        });
        ref.afterClosed().subscribe(() => URL.revokeObjectURL(imageUrl));
      },
      error: err => {
        // A lease finalized without a live session has a recorded failure
        // instead of an image, and the backend reports that as a 404.
        const msg = err?.status === 404
          ? `No ${type} screenshot for this lease — the session was already gone when it was captured.`
          : (err?.error?.error || `Error loading ${type} screenshot`);
        this.snackBar.open(msg, 'Close', { duration: 5000 });
      },
    });
  }

  /** A lease resting on an EA rate limit is slower, not dead. */
  isResting(lease: StockLease): boolean {
    return !!lease.rest_until && new Date(lease.rest_until).getTime() > Date.now();
  }

  isRunning(lease: StockLease): boolean {
    return lease.status === 'Pending' || lease.status === 'Active';
  }

  getStatusClass(status: StockLeaseStatus): string {
    switch (status) {
      case 'Pending': return 'lease-pending';
      case 'Active': return 'lease-active';
      case 'Drained': return 'lease-drained';
      case 'Released': return 'lease-released';
      case 'Failed': return 'lease-failed';
      default: return 'lease-released';
    }
  }

  /** Coins actually moved out of the stock during this run. */
  drainedCoins(lease: StockLease): number {
    return Math.max(0, (lease.start_coins ?? 0) - (lease.last_coins ?? 0));
  }

  get runningCount(): number {
    return this.leases.filter(l => this.isRunning(l)).length;
  }

  get totalCardsBought(): number {
    return this.leases.reduce((sum, l) => sum + (l.cards_bought ?? 0), 0);
  }

  formatCoins(n: number | undefined | null): string {
    if (n == null) return '—';
    return n.toLocaleString();
  }

  formatDate(d?: string): string {
    if (!d) return '—';
    return new Date(d).toLocaleString();
  }

  shortId(id: string): string {
    return id ? id.slice(0, 8) + '…' : '—';
  }

  trackByLeaseId(_index: number, l: StockLease): string {
    return l.id;
  }

  close(): void {
    this.dialogRef.close();
  }
}
